// Sayısal etiketin TOPLAMA bloğu — sağ panelde, grafiğin altında.
//
// Kullanıcı isteği: "toplama her saatlik dilim için hesaplansın, kullanıcı
// isterse özel zaman aralığını da toplayabilsin".
//
// Özel aralık ayrı bir tarih seçiciyle sorulmuyor: ekranın zaten bir aralık
// seçicisi ve yakınlaştırması var. Kullanıcının BAKTIĞI pencere, toplanan
// pencere — "gördüğüm şeyin toplamı" en az sürprizli davranış.
//
// Toplama AÇILMADAN önce önizleme gösteriliyor: "bu ayarla son penceren ne
// verirdi". Hiçbir şey yazılmıyor. "İsterse açabilsin" ancak açmadan önce
// sonucu görebiliyorsa dürüst bir seçim olur.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { tr, trTarihSaat } from '../../lib/trFormat'
import { entegrasyon, type ToplamaYontemi } from './api'

const YONTEMLER: { deger: ToplamaYontemi; ad: string; aciklama: string }[] = [
  {
    deger: 'integral',
    ad: 'Debi → miktar',
    aciklama:
      'Değer bir AKIŞ HIZI (m³/h, L/dk). Zamana göre integrali alınır: 126,5 m³/h bir saatte 126,5 m³ eder.',
  },
  {
    deger: 'fark',
    ad: 'Sayaç farkı',
    aciklama:
      'Değer KÜMÜLATİF bir sayaç. Dönemin sonu eksi başı alınır; aradaki tek bir okuma hatası sonuca yansımaz.',
  },
  {
    deger: 'ortalama',
    ad: 'Zaman ağırlıklı ortalama',
    aciklama:
      'Değer ANLIK bir büyüklük (sıcaklık, basınç). Toplamı anlamsız olduğu için doğru özet ortalamadır.',
  },
]

/** Kapsam rozeti: yarım veriyle hesaplanmış toplam sağlam sayı gibi durmasın. */
function KapsamRozeti({ kapsam }: { kapsam: number }) {
  const yuzde = kapsam * 100
  const sinif = yuzde >= 95 ? 'b-ok' : yuzde >= 70 ? 'b-amber' : 'b-red'
  return (
    <span className={`badge ${sinif}`} title="Dönemin ne kadarı gerçekten veriyle kaplandı">
      kapsam %{tr(yuzde, 0)}
    </span>
  )
}

export default function ToplamaBlogu({
  etiketId,
  bas,
  son,
}: {
  etiketId: string
  bas: string
  son: string
}) {
  const kuyruk = useQueryClient()
  const [acik, setAcik] = useState(false)
  const [yontem, setYontem] = useState<ToplamaYontemi>('integral')
  const [birim, setBirim] = useState('')

  const { data: tanim, isLoading } = useQuery({
    queryKey: ['toplama-tanim', etiketId],
    queryFn: () => entegrasyon.toplamaTanimi(etiketId),
  })

  const { data: birimler = [] } = useQuery({
    queryKey: ['hiz-birimleri'],
    queryFn: () => entegrasyon.hizBirimleri(),
    staleTime: Infinity,
  })

  const kurulu = tanim?.durum === 'aktif'

  // Aralık toplamı: kurulu tanımla ya da (kurulu değilse) önizleme ayarıyla.
  const { data: aralik } = useQuery({
    queryKey: ['toplama-aralik', etiketId, bas, son, kurulu ? 'kurulu' : yontem, kurulu ? '' : birim],
    queryFn: () =>
      entegrasyon.aralikToplami(etiketId, {
        bas,
        son,
        ...(kurulu ? {} : { yontem, birim: birim || undefined }),
      }),
    // Önizlemede birim gerekiyorsa boşken sormuyoruz — 409 dönerdi.
    enabled: kurulu || yontem !== 'integral' || birim !== '',
    retry: false,
  })

  const { data: saatlik } = useQuery({
    queryKey: ['toplama-saatlik', etiketId, bas, son],
    queryFn: () => entegrasyon.saatlikToplamlar(etiketId, { bas, son }),
    enabled: kurulu,
  })

  const kaydet = useMutation({
    mutationFn: () =>
      entegrasyon.toplamaYaz(etiketId, { yontem, okumaBirimi: birim || null }),
    onSuccess: (t) => {
      kuyruk.invalidateQueries({ queryKey: ['toplama-tanim', etiketId] })
      kuyruk.invalidateQueries({ queryKey: ['toplama-aralik', etiketId] })
      toast(
        `Toplama açıldı — saatlik dilimler her saat başı hesaplanacak` +
          (t.toplamBirimi ? ` (${t.toplamBirimi})` : ''),
      )
      setAcik(false)
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Toplama açılamadı'),
  })

  const kapat = useMutation({
    mutationFn: () => entegrasyon.toplamaKapat(etiketId),
    onSuccess: () => {
      kuyruk.invalidateQueries({ queryKey: ['toplama-tanim', etiketId] })
      // Hesaplanmış saatler SİLİNMİYOR — geçmiş kayıt, ayar kapatılınca
      // tahrif edilmemeli.
      toast('Toplama kapatıldı — hesaplanmış saatler duruyor')
    },
  })

  if (isLoading) return null

  return (
    <>
      <div className="sect-t" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>Toplama</span>
        {kurulu ? (
          <button
            type="button"
            className="btn btn-ghost"
            style={{ padding: '2px 8px', fontSize: 11, minHeight: 0, marginLeft: 'auto' }}
            onClick={() => kapat.mutate()}
          >
            Kapat
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-ghost"
            style={{ padding: '2px 8px', fontSize: 11, minHeight: 0, marginLeft: 'auto' }}
            onClick={() => setAcik((a) => !a)}
          >
            {acik ? 'Vazgeç' : 'Toplama aç'}
          </button>
        )}
      </div>

      {!kurulu && acik && (
        <div style={{ padding: '0 12px 10px' }}>
          <div className="fld">
            <label>Bu değer neyi ölçüyor?</label>
            <select
              className="in"
              value={yontem}
              onChange={(e) => setYontem(e.target.value as ToplamaYontemi)}
            >
              {YONTEMLER.map((y) => (
                <option key={y.deger} value={y.deger}>
                  {y.ad}
                </option>
              ))}
            </select>
            <div className="fld-hint">
              {YONTEMLER.find((y) => y.deger === yontem)?.aciklama}
            </div>
          </div>

          {yontem === 'integral' && (
            <div className="fld">
              <label>Okuma birimi</label>
              <select className="in" value={birim} onChange={(e) => setBirim(e.target.value)}>
                <option value="">— seçin —</option>
                {birimler.map((b) => (
                  <option key={b.birim} value={b.birim}>
                    {b.birim} → {b.toplamBirimi}
                  </option>
                ))}
              </select>
              <div className="fld-hint">
                Birim zorunlu: aynı veri m³/<b>saat</b> ile m³/<b>dakika</b> arasında
                60 kat farklı sonuç verir. Ölçek bilinmeden sayı üretilmiyor.
              </div>
            </div>
          )}

          {aralik && (
            <div className="rule">
              <b>Önizleme — hiçbir şey kaydedilmedi</b>
              <span>
                Bu ayarla seçili aralık <b>{tr(aralik.toplam, 2)}</b>
                {aralik.birim ? ` ${aralik.birim}` : ''} verirdi.
              </span>
            </div>
          )}

          <button
            type="button"
            className="btn btn-pri"
            style={{ width: '100%', justifyContent: 'center' }}
            disabled={(yontem === 'integral' && !birim) || kaydet.isPending}
            onClick={() => kaydet.mutate()}
          >
            {kaydet.isPending ? 'Açılıyor…' : 'Toplamayı aç'}
          </button>
        </div>
      )}

      {kurulu && aralik && (
        <>
          <table className="grid">
            <tbody>
              <tr>
                <td>
                  Seçili aralık toplamı
                  <div className="cell-sub">
                    {trTarihSaat(aralik.bas)} → {trTarihSaat(aralik.son)}
                  </div>
                </td>
                <td className="num mono">
                  <b>{tr(aralik.toplam, 2)}</b> {aralik.birim ?? ''}
                  <div className="cell-sub">
                    <KapsamRozeti kapsam={aralik.kapsam} />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          {saatlik && saatlik.saatler.length > 0 && (
            <>
              <div className="sect-t">Saatlik Dilimler</div>
              <table className="grid">
                <thead>
                  <tr>
                    <th>Saat</th>
                    <th className="num">Toplam</th>
                    <th className="num">Kapsam</th>
                  </tr>
                </thead>
                <tbody>
                  {saatlik.saatler.map((s) => (
                    <tr key={s.baslangic}>
                      <td className="mono">{trTarihSaat(s.baslangic)}</td>
                      <td className="num mono">
                        {tr(s.toplam, 2)} {s.birim ?? ''}
                      </td>
                      <td className="num">
                        <KapsamRozeti kapsam={s.kapsam} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="cell-sub" style={{ fontSize: 10.5, padding: '0 8px 8px' }}>
                Saatlik dilimler her saat başı hesaplanıp <b>kaydediliyor</b>; seçili
                aralık toplamı ise ham ölçümden yeniden hesaplanıyor — kısmi saatler
                bölünmüyor, gerçek saniyeleriyle giriyor.
              </div>
            </>
          )}
        </>
      )}
    </>
  )
}
